import React from 'react';
import { Trash2, Plus, MessageSquare } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const FAQForm = ({ data, onChange, submitted }) => {
  const handleSectionChange = (field, value) => {
    onChange((prev) => ({ ...prev, [field]: value }));
  };

  const handleQuestionChange = (index, field, value) => {
    onChange((prev) => {
      const newQuestions = [...(prev.questions || [])];
      newQuestions[index] = { ...newQuestions[index], [field]: value };
      return { ...prev, questions: newQuestions };
    });
  };

  const addItem = () => {
    onChange((prev) => ({
      ...prev,
      questions: [...(prev.questions || []), { title: "", description: "" }]
    }));
  };

  const removeItem = (index) => {
    onChange((prev) => ({
      ...prev,
      questions: (prev.questions || []).filter((_, i) => i !== index)
    }));
  };

  const questions = data.questions || [];

  return (
    <div className="space-y-8">
      <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 space-y-6">
        <div className="flex items-center gap-2 mb-2">
          <div className="p-2 bg-primary/10 text-primary rounded-lg">
            <MessageSquare size={20} />
          </div>
          <h3 className="font-bold text-slate-800">FAQ Section Headers</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>FAQ Section Title <span className="text-red-500">*</span></Label>
            <Input 
              value={data.title} 
              className={(!data.title && submitted) ? "border-red-500" : ""}
              onChange={(e) => handleSectionChange('title', e.target.value)} 
              placeholder="e.g. Frequently Asked Questions"
            />
          </div>
          <div className="space-y-2">
            <Label>FAQ Section Subtitle</Label>
            <Input 
              value={data.sub_title} 
              onChange={(e) => handleSectionChange('sub_title', e.target.value)} 
              placeholder="e.g. Find answers to common questions about this project."
            />
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <Label className="text-lg font-bold flex items-center gap-2">
            Questions & Answers <span className="text-red-500">*</span>
          </Label>
          <button 
            type="button" 
            onClick={addItem}
            className="flex items-center gap-1 bg-primary/10 text-primary px-3 py-1.5 rounded-lg text-sm font-semibold hover:bg-primary/20 transition-all"
          >
            <Plus size={16} /> Add Q&A
          </button>
        </div>

        {questions.length === 0 && submitted && (
          <p className="text-xs text-red-500 font-medium italic">Please add at least one question and answer.</p>
        )}

        <div className="space-y-4">
          {questions.map((item, index) => (
            <div key={index} className={`bg-white p-6 rounded-2xl border relative group transition-all ${submitted && (!item.title || !item.description) ? "border-red-500 shadow-sm shadow-red-50" : "border-slate-200"}`}>
              <button 
                type="button" 
                onClick={() => removeItem(index)}
                className="absolute top-4 right-4 text-red-400 hover:text-red-600 transition-colors"
              >
                <Trash2 size={18} />
              </button>
              <div className="space-y-4">
                <div className="pr-10">
                  <Label className="mb-1.5 block text-xs font-semibold text-slate-500 uppercase tracking-wider">Question {index + 1} <span className="text-red-500">*</span></Label>
                  <Input 
                    value={item.title} 
                    className={(!item.title && submitted) ? "border-red-500" : ""}
                    onChange={(e) => handleQuestionChange(index, 'title', e.target.value)} 
                    placeholder="The Question..."
                  />
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs font-semibold text-slate-500 uppercase tracking-wider">Answer <span className="text-red-500">*</span></Label>
                  <textarea 
                    className={`w-full h-24 px-4 py-3 rounded-xl border focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm ${(!item.description && submitted) ? "border-red-500" : "border-slate-200"}`}
                    value={item.description} 
                    onChange={(e) => handleQuestionChange(index, 'description', e.target.value)} 
                    placeholder="The Answer or insight..."
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {questions.length > 0 && (
          <button 
            type="button" 
            onClick={addItem}
            className="w-full py-4 border-2 border-dashed border-slate-200 rounded-2xl text-slate-400 hover:border-primary/50 hover:text-primary transition-all flex items-center justify-center gap-2 font-medium"
          >
            <Plus size={20} /> Add Another Q&A
          </button>
        )}
      </div>
    </div>
  );
};

export default FAQForm;
